import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NewTaskSubTaskList } from 'shared/components/connected/sub-tasks-list';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { useNewTaskState } from 'shared/hooks/use-new-task-state';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { NewGoalWithID } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import { NewTagWithID } from 'shared/types/tag';
import { CreateTask, NewSubTask, NewTask } from 'shared/types/task';
import { getIsMacOs } from 'shared/utils/get-is-mac-os';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { v4 as uuidv4 } from 'uuid';

import { CreateMain } from './components/create-main';
import { TaskDescription } from './components/task-description';
import { Meta } from './meta';
import * as Styled from './task-create.style';
import { MetaViewOptions } from './types';
import { useAddGoalToCreate, useAddTagToCreate, useData } from './use-data';

const noop = () => {};

export type TaskCreateProps = {
  name: string;
  updateName: (name: string) => void;
  onCreate: (taskInformation: {
    task: NewTask;
    createTask: CreateTask;
    relationships: Partial<Record<IDPrefixes, ID[] | null>>;
    subTasks: NewSubTask[];
    goalsToCreate: NewGoalWithID[];
    tagsToCreate: NewTagWithID[];
  }) => void;
  onClose: () => void;
  initialValues?: Partial<CreateTask>;
  dateAnchored?: boolean;
  noBreakpoints?: boolean;
};

export const TaskCreate: React.FC<TaskCreateProps> = ({
  name,
  updateName,
  onCreate,
  onClose,
  initialValues,
  dateAnchored,
  noBreakpoints,
}) => {
  const { t } = useTranslation();
  const locale = useI18nLocale();
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const {
    lifeAreas,
    goals,
    tags,
    dateFormat,
    timeFormat,
    createHabitAllowed,
    createHabitTargetAllowed,
    goalsToCreate,
    tagsToCreate,
    openPremiumDialog,
  } = useData();
  const addGoalToCreate = useAddGoalToCreate();
  const addTagToCreate = useAddTagToCreate();

  const {
    updateIgnoredWords,
    updateWordIds,
    task,
    subTasks,
    updateSubTasks,
    newSubTask,
    updateNewSubTask,
    updateTaskVariables,
  } = useNewTaskState(initialValues, {
    nlpString: name,
    updateNlpString: updateName,
    lifeAreas,
    goals,
    tags,
    dateFormat,
    timeFormat,
    createHabitAllowed,
    scheduleTargetAllowed: createHabitTargetAllowed,
    language: locale,
    dateAnchored,
  });

  const isMac = getIsMacOs();

  const _updateName = useCallback(
    (
      name: string,
      { wordIds, ignoredWordIds }: { wordIds: WordId[]; ignoredWordIds: ID[] },
    ) => {
      updateName(name);
      updateWordIds(wordIds);
      updateIgnoredWords(ignoredWordIds);
    },
    [updateIgnoredWords, updateName, updateWordIds],
  );

  const _updateDescription = (description: string) =>
    updateTaskVariables({ description });

  const _updateTagRelationships = (relationshipIds: ID[]) =>
    updateTaskVariables({ tagIds: relationshipIds });

  const _onClose = () => onClose();

  const submit = () => {
    const { tagIds, ..._task } = task;
    onCreate({
      task: {
        ..._task,
        startDate: new Date(),
        createdAt: new Date(),
        endStrategy: task.endStrategy
          ? {
              deadline: task.endStrategy?.deadline ?? null,
              completionCount: task.endStrategy?.completionCount ?? null,
            }
          : null,
      } as NewTask,
      createTask: task,
      subTasks: [
        ...subTasks,
        ...(newSubTask ? [{ id: uuidv4(), name: newSubTask }] : []),
      ],
      relationships: { [IDPrefixes.Tag]: tagIds },
      goalsToCreate: goalsToCreate.filter(({ id }) => _task.goalId === id),
      tagsToCreate: tagsToCreate.filter(({ id }) => tagIds?.includes(id)),
    });
  };

  const updateNameAndSubmit = (name: string) => {
    if (!name) {
      return;
    }

    updateName(name);
    queueMicrotask(() => {
      submit();
      onClose();
    });
  };

  useModifierKeysDown('Enter', (e) => {
    e.preventDefault();
    queueMicrotask(submit);
  });

  return (
    <Styled.Container>
      <Styled.Body $noBreakpoints={!!noBreakpoints}>
        <Styled.Main>
          <Styled.MainBody $paddingTop>
            <CreateMain
              name={name}
              priority={task.priority}
              updateName={_updateName}
              onNameSubmit={updateNameAndSubmit}
              onNewGoal={addGoalToCreate}
              onNewTag={addTagToCreate}
              onPremium={openPremiumDialog}
            />
            <Styled.BodyContent>
              <Styled.DescriptionContainer>
                <TaskDescription
                  description={task.description}
                  updateDescription={_updateDescription}
                  instantUpdate
                />
              </Styled.DescriptionContainer>

              <Styled.SubTaskListContainer>
                <NewTaskSubTaskList
                  subTasks={subTasks}
                  onChangeSubTasks={updateSubTasks}
                  // keep track of the new subTask for when cmd+enter is pressed and the focus was on the new subTask input
                  updateNewSubTask={updateNewSubTask}
                />
              </Styled.SubTaskListContainer>
            </Styled.BodyContent>
          </Styled.MainBody>
        </Styled.Main>

        <Styled.SidePanel $noBreakpoints={!!noBreakpoints}>
          <Meta
            task={task}
            updateTask={updateTaskVariables}
            updateRelationships={_updateTagRelationships}
            view={MetaViewOptions.Settings}
            updateView={noop}
            isCreate
          />
        </Styled.SidePanel>
      </Styled.Body>

      <Styled.Footer>
        <Button
          variant={Variants.Contained}
          size={Sizes.Medium}
          onClick={_onClose}
          color={Colors.Inherit}
        >
          {t('general.labels.cancel')}
        </Button>
        {isMac && (
          <Tooltip
            label={
              <Styled.TooltipContent>
                {t('general.labels.create-task')}{' '}
                <Styled.TooltipContentButton $wide={!isMac}>
                  {isMac ? '⌘' : 'CTRL'}
                </Styled.TooltipContentButton>{' '}
                <Styled.TooltipContentButton>⏎</Styled.TooltipContentButton>
              </Styled.TooltipContent>
            }
            referenceElement={saveButtonRef}
          />
        )}
        <Button
          ref={saveButtonRef}
          variant={Variants.Contained}
          size={Sizes.Medium}
          color={Colors.Primary}
          onClick={submit}
          disabled={!task.name.trim()}
        >
          {t('general.labels.create')}
        </Button>
      </Styled.Footer>
    </Styled.Container>
  );
};
